import React, { useState } from "react";
import { useFormikContext } from "formik";
import get from "lodash/get";
import { FORM_KEY } from "utils/constants/form";
import SectionBezel from "components/SectionBezel";
import { STATE_KEYS, TRIAL_PERIOD_DAYS } from "utils/constants/state";
import TrialPeriodDays from "components/MerchantAccountConfigForm/Fields/TrialPeriodDays";
import { shortLinkHasRecurringPrices } from "utils/shortLink";
import CheckoutAttributeToggle from "components/CheckoutAttributeToggle";
import { CHECKOUT_SESSION } from "utils/constants/stripe";
import { getCheckoutAttributesConfig } from "utils/checkout/attributes";
import { useShortLinkTrial } from "utils/hooks/shortLink";
import InheritenceDisplay from "components/InheritenceDisplay";

const DEFAULT_TITLE = "Trial";
const PROMPT = {
  ADD_RECURRING:
    "You need to add a recurring price before you can apply trial settings.",
  INVALID:
    "Trials can only be added to checkouts which contain at least one recurring price."
};

const TrialFields = (
  { name, skipRequirements, inherited, title } = { title: DEFAULT_TITLE }
) => {
  const ctxTitle = title || DEFAULT_TITLE;
  const [open, setOpen] = useState(false);
  const { values, setFieldValue } = useFormikContext();

  const {
    error,
    inheritedTrialPeriodDays,
    activeInheritence
  } = useShortLinkTrial({ inherited });
  const trialPeriodDaysFormPath = `${FORM_KEY}.${TRIAL_PERIOD_DAYS}`;
  if (activeInheritence) {
    return (
      <InheritenceDisplay
        title={ctxTitle}
        error={error}
        onRemove={() => {
          setFieldValue(STATE_KEYS.SHORT_LINK.TRIAL_PERIOD_DAYS, 0);
          setFieldValue(trialPeriodDaysFormPath, true);
        }}
      >
        <div className="flex flex-column justify-between items-start pt3">
          <div className="f6 fw5">Days</div>
          <div className="f6 pt2 word-break">{inheritedTrialPeriodDays}</div>
        </div>
      </InheritenceDisplay>
    );
  } else {
    const hasRecurringPrices = shortLinkHasRecurringPrices(values);
    const trialPeriodDaysFormActive = get(values, trialPeriodDaysFormPath);

    let content;
    const recurringPriceRequired = !skipRequirements && !hasRecurringPrices;
    if (recurringPriceRequired) {
      content = (
        <div className="f5 lh-copy">
          {values.id ? PROMPT.INVALID : PROMPT.ADD_RECURRING}
        </div>
      );
    } else {
      content = (
        <TrialPeriodDays
          disabled={!trialPeriodDaysFormActive}
          name={name}
          copy={["Trial length in days for recurring prices on this checkout."]}
          customClasses={{
            container:
              "flex flex-column flex-row-ns justify-between-ns items-center-ns",
            copyWrapper: "flex flex-column flex-shrink-0"
          }}
        />
      );
    }

    const toggleProps = {
      disabled: recurringPriceRequired,
      attributes: getCheckoutAttributesConfig({
        name,
        attributes: [CHECKOUT_SESSION.TRIAL_PERIOD_DAYS]
      }),
      open,
      onChange: (updatedOpen) => {
        updatedOpen && setOpen(updatedOpen);
        setFieldValue(trialPeriodDaysFormPath, updatedOpen);
      }
    };

    return (
      <SectionBezel
        header={ctxTitle}
        action={<CheckoutAttributeToggle {...toggleProps} />}
        expandable
        isOpen={open}
        setOpen={setOpen}
      >
        {content}
      </SectionBezel>
    );
  }
};

export default TrialFields;
